import { getDatabase, ref, get, update } from 'firebase/database';
import { formatDateForStorage } from '../Helpers/DateUtils';
import type { MaintenancePlan } from '../Helpers/maintenanceCostUtils';
import { requireAdmin } from './adminGuard';
import { recordActivity } from './activityApi';

/**
 * Activation and archiving of the Underhållsplan (`maintenance/active|archive/{b}`).
 * Both are admin only, here and in the database rules (molnfast-react#1570).
 * `maintenance/archive/{b}/{key}` is write-once by rule: an archived plan is the
 * activation history the timeline derives from, so it is never rewritten.
 */

export interface ActivatePlanInput {
    buildingKey: string;
    /**
     * Builds the new active plan from the current one, read fresh (null when
     * there is none). The API stamps `metadata.activatedAt`/`activatedBy`.
     */
    buildPlan: (current: MaintenancePlan | null) => MaintenancePlan;
    /** Further paths written in the same root update (the Skickbedömning schedule). */
    extraUpdates?: Record<string, unknown>;
}

/** Archive key: the Stockholm date with separators made path-safe, `YYYY-MM-DD-hh-mm-ss`. */
const archiveKey = (formattedNow: string): string => formattedNow.replace(/[:\s]/g, '-');

const readActive = async (buildingKey: string): Promise<MaintenancePlan | null> => {
    const snapshot = await get(ref(getDatabase(), `maintenance/active/${buildingKey}`));
    return snapshot.exists() ? (snapshot.val() as MaintenancePlan) : null;
};

const archiveUpdate = (buildingKey: string, plan: MaintenancePlan, uid: string, formattedNow: string) => ({
    [`maintenance/archive/${buildingKey}/${archiveKey(formattedNow)}`]: {
        ...plan,
        archivedAt: formattedNow,
        archivedBy: uid
    }
});

export const maintenancePlanApi = {
    /**
     * Activate a plan from the draft in one root update: the current active plan
     * moves to the archive, the new one becomes active and the draft is removed.
     */
    async activatePlan({ buildingKey, buildPlan, extraUpdates = {} }: ActivatePlanInput): Promise<void> {
        const db = getDatabase();
        const uid = await requireAdmin(db, 'aktivera underhållsplanen');
        const current = await readActive(buildingKey);
        const plan = buildPlan(current);
        const now = formatDateForStorage(new Date());

        await update(ref(db), {
            ...extraUpdates,
            ...(current ? archiveUpdate(buildingKey, current, uid, now) : {}),
            [`maintenance/active/${buildingKey}`]: {
                ...plan,
                metadata: { ...plan.metadata, activatedAt: now, activatedBy: uid }
            },
            [`maintenance/drafts/${buildingKey}`]: null
        });
        recordActivity('maintenancePlan.activated', { buildingKey, targetKey: buildingKey });
    },

    /** Move the active plan to the archive, leaving the building without an active plan. */
    async archivePlan(buildingKey: string): Promise<void> {
        const db = getDatabase();
        const uid = await requireAdmin(db, 'arkivera underhållsplanen');
        const current = await readActive(buildingKey);
        if (!current) throw new Error('Det finns ingen aktiv underhållsplan att arkivera');

        await update(ref(db), {
            ...archiveUpdate(buildingKey, current, uid, formatDateForStorage(new Date())),
            [`maintenance/active/${buildingKey}`]: null
        });
        recordActivity('maintenancePlan.archived', { buildingKey, targetKey: buildingKey });
    }
};
